/*
 * The SEO, GEO, AEO tracker: every page of the site built to be found, what it targets, what it
 * tells search engines and AI assistants, and everything that has changed on it. Super Admin and
 * Marketing (owner, 2026-10-21). Built from the shared CRM list pieces only.
 */
import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import ErrorMessage from '@components/Common/ErrorMessage';
import FilterBar, { type AppliedFilter } from '@components/Common/FilterBar';
import SearchInput from '@components/Common/SearchInput';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import StatTile from '@components/Common/StatTile';
import MintlyTable from '@components/Common/Table';
import { useToast } from '@components/Common/Toast/ToastProvider';
import ActionIconButton from '@components/Common/ActionIconButton';
import AutocompleteFormField from '@components/Common/AutocompleteFormField';
import { useCanEditMarketing } from '@components/Common/Utils/roles';
import ViewOnlyChip from '@components/Common/ViewOnlyChip';
import {
  syncSearchPerformanceMutation,
  syncSeoInventoryMutation,
} from '@constants/graphQLQueries';
import { formatDate, formatDateTime, formatRelative } from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import WebOutlinedIcon from '@mui/icons-material/WebOutlined';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { downloadCsv } from '../Referrals/payoutExport';
import ChangeValue from './ChangeValue';
import SeoCategoriesTab from './SeoCategoriesTab';
import SeoKeywordsTab from './SeoKeywordsTab';
import {
  EMPTY_PAGES_STATE,
  EVENT_CHIP,
  FACET_FILTERS,
  type KeywordsState,
  PAGE_SIZE,
  PAGE_SORTS,
  PAGE_SORT_LABEL,
  type PageSort,
  type PagesState,
  type SeoEvent,
  type SeoFacet,
  type SeoFacets,
  type SeoPage,
  type SeoPageTotals,
  type SeoSummary,
  type SeoSyncState,
  TYPE_LABEL,
  changeText,
  clicksChange,
  fetchEvents,
  fetchFacets,
  fetchPages,
  fetchSummary,
  fmtCount,
  fmtPosition,
  indexChip,
  keywordsCsv,
  pageIdOf,
  pagesCsv,
  parseKeywordsParams,
  parsePagesParams,
  syncJustFinished,
  syncOutcome,
  toKeywordsParams,
  totalsText,
  toPagesParams,
  typeLabel,
} from './seoData';
import './index.scss';

const TABS = ['pages', 'categories', 'keywords', 'activity'] as const;
type Tab = (typeof TABS)[number];

const typeOptions = [
  { value: '', label: 'All types' },
  ...Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label })),
];
const flagOptions = [
  { value: '', label: 'Any' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];
const sortOptions = PAGE_SORTS.map((value) => ({
  value,
  label: PAGE_SORT_LABEL[value],
}));
const kindOptions = [
  { value: 'CHANGED', label: 'Changes' },
  { value: 'NOTE', label: 'Notes' },
  { value: 'ADDED', label: 'Pages added' },
  { value: 'REMOVED', label: 'Pages removed' },
  { value: '', label: 'Everything' },
];

const DEFAULT_KIND = 'CHANGED';

/** How often the page asks whether a running sync has finished. */
const SYNC_POLL_MS = 5000;

const facetLabel = (f: SeoFacet) => `${f.value} (${f.count})`;

const errorText = (e: unknown, fallback: string) =>
  String((e as { message?: string })?.message ?? '').replace(
    /^GraphQL errors:\s*/,
    '',
  ) || fallback;

const PagesTab: React.FC<{
  state: PagesState;
  setState: (s: PagesState) => void;
  summary: SeoSummary | null;
  summaryLoading: boolean;
  reloadKey: number;
}> = ({ state, setState, summary, summaryLoading, reloadKey }) => {
  const navigate = useNavigate();
  const [qDraft, setQDraft] = useState(state.q);
  const [rows, setRows] = useState<SeoPage[]>([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState<SeoPageTotals | null>(null);
  const [facets, setFacets] = useState<SeoFacets | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setQDraft(state.q), [state.q]);

  // The filter values change only when the site is read again, so once per sync is enough.
  useEffect(() => {
    let live = true;
    fetchFacets()
      .then((f) => live && setFacets(f))
      .catch(() => live && setFacets(null));
    return () => {
      live = false;
    };
  }, [reloadKey]);

  const latest = useRef(0);
  const load = useCallback(async () => {
    const call = ++latest.current;
    setLoading(true);
    setError(null);
    try {
      const r = await fetchPages(state);
      if (call !== latest.current) return;
      setRows(r?.content ?? []);
      setTotal(r?.totalElements ?? 0);
      setTotals(r?.totals ?? null);
    } catch (e) {
      if (call === latest.current)
        setError(errorText(e, 'The pages could not be loaded.'));
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [state]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const applied: AppliedFilter[] = [
    state.q && {
      key: 'q',
      label: `Search: ${state.q}`,
      onClear: () => setState({ ...state, q: '', page: 0 }),
    },
    state.pageType && {
      key: 'type',
      label: `Type: ${typeLabel(state.pageType)}`,
      onClear: () => setState({ ...state, pageType: '', page: 0 }),
    },
    state.indexable && {
      key: 'idx',
      label: `Indexable: ${state.indexable}`,
      onClear: () => setState({ ...state, indexable: '', page: 0 }),
    },
    state.llms && {
      key: 'llms',
      label: `In llms.txt: ${state.llms}`,
      onClear: () => setState({ ...state, llms: '', page: 0 }),
    },
    state.notIndexed && {
      key: 'notIndexed',
      label: 'Not indexed by Google',
      onClear: () => setState({ ...state, notIndexed: false, page: 0 }),
    },
    state.dropping && {
      key: 'dropping',
      label: 'Dropping',
      onClear: () => setState({ ...state, dropping: false, page: 0 }),
    },
    ...FACET_FILTERS.map(
      (f) =>
        state[f.key] && {
          key: f.key,
          label: `${f.label}: ${state[f.key]}`,
          onClear: () => setState({ ...state, [f.key]: '', page: 0 }),
        },
    ),
    state.sort && {
      key: 'sort',
      label: `Sort: ${PAGE_SORT_LABEL[state.sort]}`,
      onClear: () => setState({ ...state, sort: '', page: 0 }),
    },
  ].filter(Boolean) as AppliedFilter[];
  const searchSynced = !!summary?.searchSyncedAt;

  return (
    <Box className="seo-list__tab">
      <Box className="cc-stat-grid">
        <StatTile
          label="Pages tracked"
          value={summary?.total ?? '-'}
          icon={<WebOutlinedIcon />}
          sublabel={
            summary
              ? `${fmtCount(summary.indexable)} indexable, ${fmtCount(summary.inLlms)} in llms.txt`
              : undefined
          }
          loading={summaryLoading}
        />
        <StatTile
          label="Clicks (28d)"
          icon={<TouchAppOutlinedIcon />}
          value={searchSynced ? fmtCount(summary?.clicks28) : '-'}
          sublabel={
            searchSynced
              ? `${fmtCount(summary?.impressions28)} impressions on Google`
              : 'After the first Google sync'
          }
          loading={summaryLoading}
        />
        <StatTile
          label="Not indexed"
          icon={<ManageSearchOutlinedIcon />}
          value={searchSynced ? fmtCount(summary?.notIndexed) : '-'}
          tone={(summary?.notIndexed ?? 0) > 0 ? 'warning' : 'neutral'}
          sublabel={
            searchSynced
              ? `Google has not indexed them. ${fmtCount(summary?.dropping)} dropping.`
              : 'After the first Google sync'
          }
          to={(summary?.notIndexed ?? 0) > 0 ? '/seo?notIndexed=1' : undefined}
          linkLabel="Show the pages Google has not indexed"
          loading={summaryLoading}
        />
        {summary && !summary.ga4Connected ? (
          <StatTile
            label="Google Analytics"
            icon={<InsightsOutlinedIcon />}
            value="Not connected"
            sublabel="Connect Google Analytics to see visits from AI assistants and sign-ups"
            loading={summaryLoading}
          />
        ) : (
          <>
            <StatTile
              label="Visits from AI assistants (28d)"
              icon={<AutoAwesomeOutlinedIcon />}
              value={fmtCount(summary?.aiSessions28)}
              sublabel="ChatGPT, Perplexity, Gemini and others"
              loading={summaryLoading}
            />
            <StatTile
              label="Sign-ups (28d)"
              icon={<HowToRegOutlinedIcon />}
              value={fmtCount(summary?.signups28)}
              sublabel={`From ${fmtCount(summary?.sessions28)} visits to these pages`}
              loading={summaryLoading}
            />
          </>
        )}
      </Box>

      <FilterBar
        applied={applied}
        onApply={() => setState({ ...state, q: qDraft.trim(), page: 0 })}
        onClearAll={() => {
          setQDraft('');
          setState(EMPTY_PAGES_STATE);
        }}
        onCancel={() => setQDraft(state.q)}
      >
        <Box className="seo-list__search">
          <SearchInput
            value={qDraft}
            onChange={setQDraft}
            placeholder="Search page, keyword or title"
            onClear={() => setQDraft('')}
          />
        </Box>
        <SelectFormField
          required={false}
          name="seo-type"
          label="Type"
          options={typeOptions}
          value={state.pageType}
          onChange={(_n, v) =>
            setState({ ...state, pageType: String(v), page: 0 })
          }
        />
        {FACET_FILTERS.map((f) => {
          const options = facets?.[f.facets] ?? [];
          const current = state[f.key];
          // A value from a shared link that no LIVE page has any more still shows as selected.
          const selected = current
            ? (options.find((o) => o.value === current) ?? { value: current, count: 0 })
            : null;
          return (
            <Box key={f.key} className="seo-list__facet">
              <AutocompleteFormField
                required={false}
                name={`seo-${f.key}`}
                label={f.label}
                size="small"
                placeholder={`Any ${f.label.toLowerCase()}`}
                options={options}
                value={selected}
                getOptionLabel={(o: SeoFacet) => (o ? facetLabel(o) : '')}
                isOptionEqualToValue={(o: SeoFacet, v: SeoFacet) => o?.value === v?.value}
                onChange={(_e: unknown, nv: SeoFacet | null) =>
                  setState({ ...state, [f.key]: nv?.value ?? '', page: 0 })
                }
              />
            </Box>
          );
        })}
        <SelectFormField
          required={false}
          name="seo-indexable"
          label="Indexable"
          options={flagOptions}
          value={state.indexable}
          onChange={(_n, v) =>
            setState({
              ...state,
              indexable: String(v) as PagesState['indexable'],
              page: 0,
            })
          }
        />
        <SelectFormField
          required={false}
          name="seo-llms"
          label="In llms.txt"
          options={flagOptions}
          value={state.llms}
          onChange={(_n, v) =>
            setState({
              ...state,
              llms: String(v) as PagesState['llms'],
              page: 0,
            })
          }
        />
        <SelectFormField
          required={false}
          name="seo-sort"
          label="Sort by"
          options={sortOptions}
          value={state.sort}
          onChange={(_n, v) =>
            setState({ ...state, sort: String(v) as PageSort, page: 0 })
          }
        />
        <FormControlLabel
          className="seo-list__check"
          control={
            <Checkbox
              size="small"
              checked={state.notIndexed}
              onChange={(e) =>
                setState({ ...state, notIndexed: e.target.checked, page: 0 })
              }
            />
          }
          label="Not indexed by Google"
        />
        <FormControlLabel
          className="seo-list__check"
          control={
            <Checkbox
              size="small"
              checked={state.dropping}
              onChange={(e) =>
                setState({ ...state, dropping: e.target.checked, page: 0 })
              }
            />
          }
          label="Dropping"
        />
      </FilterBar>

      {!loading && !error && totals && totals.pages > 0 && (
        <Box className="seo-list__totals" aria-label="Totals for the pages matching these filters">
          <span className="seo-list__totals-label">
            {applied.length ? 'Matching pages, last 28 days' : 'All pages, last 28 days'}
          </span>
          {totalsText(totals, !!summary?.ga4Connected).map((t) => (
            <span key={t} className="seo-list__totals-item">
              {t}
            </span>
          ))}
        </Box>
      )}

      {loading ? (
        <SkeletonLoader rows={10} columns={8} />
      ) : error ? (
        <ErrorMessage message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={
            applied.length
              ? 'No pages match these filters'
              : 'No pages tracked yet'
          }
          body={
            applied.length
              ? 'Clear a filter to see more.'
              : 'Run "Sync now" to read the site for the first time.'
          }
        />
      ) : (
        <MintlyTable
          pagination={{
            enabled: total > PAGE_SIZE,
            page: state.page,
            rowsPerPage: PAGE_SIZE,
            totalNumberOfRows: total,
            handleChangePage: (_e: unknown, page: number) =>
              setState({ ...state, page }),
          }}
          rows={rows.map((r) => ({ ...r, id: r.path }))}
          selectors={[
            'page',
            'clicks',
            'position',
            'google',
            'sessions',
            'signups',
            'type',
            'changed',
          ]}
          columns={{
            page: { card: 'title', tablet: true },
            google: { card: 'status', tablet: true },
            clicks: { card: 'fact', tablet: true },
            position: { card: 'fact' },
            sessions: { card: 'fact' },
            signups: { card: 'fact' },
            type: { card: 'subtitle' },
            changed: { card: 'hidden' },
          }}
          onRowClick={(row: SeoPage) =>
            navigate(`/seo/pages/${pageIdOf(row.path)}`)
          }
          tableHeadRow={{
            page: <>Page</>,
            clicks: <>Clicks (28d)</>,
            position: <>Avg position</>,
            google: <>Google index</>,
            sessions: <>Sessions (28d)</>,
            signups: <>Sign-ups (28d)</>,
            type: <>Type</>,
            changed: <>Last change</>,
          }}
          tableBodyRow={{
            page: (row: SeoPage) => (
              <Box className="seo-list__page">
                <strong>{row.path}</strong>
                {row.primaryKeyword && (
                  <span className="seo-list__keyword">{row.primaryKeyword}</span>
                )}
                {row.fetchError && (
                  <span className="seo-list__warn">
                    Could not be read on the last sync
                  </span>
                )}
              </Box>
            ),
            clicks: (row: SeoPage) =>
              row.clicks28 === null ? (
                <>-</>
              ) : (
                <span className="seo-list__metric">
                  {fmtCount(row.clicks28)}
                  {row.dropping && row.clicksPrev28 !== null && (
                    <ChangeValue
                      change={clicksChange(row.clicks28 - row.clicksPrev28)}
                      label="Against the previous 28 days"
                    />
                  )}
                </span>
              ),
            position: (row: SeoPage) => <>{fmtPosition(row.position28)}</>,
            google: (row: SeoPage) => {
              const chip = indexChip(row.googleIndex);
              return <CRMStatusChip status={chip.status} label={chip.label} />;
            },
            sessions: (row: SeoPage) => <>{fmtCount(row.sessions28)}</>,
            signups: (row: SeoPage) => <>{fmtCount(row.signups28)}</>,
            type: (row: SeoPage) => <>{typeLabel(row.pageType)}</>,
            changed: (row: SeoPage) => <>{formatRelative(row.lastChangedAt)}</>,
          }}
        />
      )}
    </Box>
  );
};

const ActivityTab: React.FC<{ reloadKey: number }> = ({ reloadKey }) => {
  const navigate = useNavigate();
  const [kind, setKind] = useState(DEFAULT_KIND);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<SeoEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const latest = useRef(0);
  const load = useCallback(async () => {
    const call = ++latest.current;
    setLoading(true);
    setError(null);
    try {
      const r = await fetchEvents(kind, page);
      if (call !== latest.current) return;
      setRows(r?.content ?? []);
      setTotal(r?.totalElements ?? 0);
    } catch (e) {
      if (call === latest.current)
        setError(errorText(e, 'The activity could not be loaded.'));
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [kind, page]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  return (
    <Box className="seo-list__tab">
      <FilterBar
        applied={
          kind === DEFAULT_KIND
            ? []
            : [
                {
                  key: 'kind',
                  label: `Show: ${kindOptions.find((k) => k.value === kind)?.label ?? kind}`,
                  onClear: () => setKind(DEFAULT_KIND),
                },
              ]
        }
        onApply={() => setPage(0)}
        onClearAll={() => {
          setKind(DEFAULT_KIND);
          setPage(0);
        }}
      >
        <SelectFormField
          required={false}
          name="seo-kind"
          label="Show"
          options={kindOptions}
          value={kind}
          onChange={(_n, v) => {
            setKind(String(v));
            setPage(0);
          }}
        />
      </FilterBar>
      {loading ? (
        <SkeletonLoader rows={8} columns={5} />
      ) : error ? (
        <ErrorMessage message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing here yet"
          body="Changes appear after each sync; notes appear when someone writes one on a page."
        />
      ) : (
        <MintlyTable
          pagination={{
            enabled: total > PAGE_SIZE,
            page,
            rowsPerPage: PAGE_SIZE,
            totalNumberOfRows: total,
            handleChangePage: (_e: unknown, p: number) => setPage(p),
          }}
          rows={rows}
          selectors={['what', 'page', 'kind', 'when', 'by']}
          onRowClick={(row: SeoEvent) =>
            navigate(`/seo/pages/${pageIdOf(row.path)}`)
          }
          tableHeadRow={{
            what: <>What happened</>,
            page: <>Page</>,
            kind: <>Kind</>,
            when: <>When</>,
            by: <>By</>,
          }}
          tableBodyRow={{
            what: (row: SeoEvent) => (
              <Box className="seo-list__event">
                <strong>{row.text}</strong>
                {row.changes.map((c) => (
                  <span key={c.field} className="seo-list__change">
                    {changeText(c)}
                  </span>
                ))}
              </Box>
            ),
            page: (row: SeoEvent) => <>{row.path}</>,
            kind: (row: SeoEvent) => (
              <CRMStatusChip
                status={EVENT_CHIP[row.kind]?.status ?? 'new'}
                label={EVENT_CHIP[row.kind]?.label ?? row.kind}
              />
            ),
            when: (row: SeoEvent) => <>{formatDate(row.at)}</>,
            by: (row: SeoEvent) => <>{row.by ?? '-'}</>,
          }}
        />
      )}
    </Box>
  );
};

const Seo: React.FC = () => {
  const { showToast } = useToast();
  // An HR Lead reads the tracker (owner, 2026-11-03). Both syncs are writes and are hidden from
  // her; the proxy refuses them too (SEO_TRACKER_WRITE_FIELDS). Export is a read, so it stays.
  const canEdit = useCanEditMarketing();
  const [params, setParams] = useSearchParams();
  const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '')
    ? (params.get('tab') as Tab)
    : 'pages';
  const state = useMemo(() => parsePagesParams(params), [params]);
  const keywords = useMemo(() => parseKeywordsParams(params), [params]);
  const [summary, setSummary] = useState<SeoSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [startingSync, setStartingSync] = useState(false);
  const [startingGoogle, setStartingGoogle] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let live = true;
    setSummaryLoading(true);
    fetchSummary()
      .then((r) => live && setSummary(r))
      .catch(() => live && setSummary(null))
      .finally(() => live && setSummaryLoading(false));
    return () => {
      live = false;
    };
  }, [reloadKey]);

  const setState = (s: PagesState) => setParams(toPagesParams(s, tab));
  const setKeywords = (s: KeywordsState) => setParams(toKeywordsParams(s));
  const role = params.get('role') ?? '';
  const setRole = (r: string) =>
    setParams(r ? { tab: 'categories', role: r } : { tab: 'categories' });
  const onTab = (v: number) =>
    setParams(
      TABS[v] === 'keywords'
        ? toKeywordsParams(keywords)
        : TABS[v] === 'categories'
          ? role
            ? { tab: 'categories', role }
            : { tab: 'categories' }
          : toPagesParams(state, TABS[v]),
    );

  /*
   * Both syncs take minutes, so starting one only starts it: the backend returns its state and
   * this page asks again every few seconds until it is no longer running. The wait used to sit
   * inside the request, and the edge gave up after 30 seconds with a 504 while the sync went on.
   * Polling follows the summary, so a sync started by the nightly run or another admin also
   * disables the button and reports when it is done.
   */
  const syncRunning = !!summary?.inventorySync?.running;
  const googleRunning = !!summary?.searchSync?.running;
  useEffect(() => {
    if (!syncRunning && !googleRunning) return undefined;
    const before = summary;
    const timer = window.setTimeout(async () => {
      try {
        const next = await fetchSummary();
        if (!next) return;
        let finished = false;
        if (syncJustFinished(before?.inventorySync, next.inventorySync)) {
          const o = syncOutcome(next.inventorySync as SeoSyncState, 'The site was read.');
          showToast(o.message, o.tone);
          finished = true;
        }
        if (syncJustFinished(before?.searchSync, next.searchSync)) {
          const o = syncOutcome(next.searchSync as SeoSyncState, 'Google data refreshed.');
          showToast(o.message, o.tone);
          finished = true;
        }
        if (finished) setReloadKey((k) => k + 1);
        else setSummary(next);
      } catch {
        // A failed poll is not the sync failing: ask again on the next tick.
        setPollTick((t) => t + 1);
      }
    }, SYNC_POLL_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, pollTick, syncRunning, googleRunning]);

  const startSync = async (
    mutation: string,
    field: 'syncSeoInventory' | 'syncSearchPerformance',
    stateKey: 'inventorySync' | 'searchSync',
    setStarting: (b: boolean) => void,
    startedText: string,
    failedText: string,
  ) => {
    setStarting(true);
    try {
      const r = await fetchGraphQLApiData(mutation);
      const st = (r?.data?.[field] ?? null) as SeoSyncState | null;
      if (st) setSummary((prev) => (prev ? { ...prev, [stateKey]: st } : prev));
      if (st?.running) showToast(startedText, 'info');
    } catch (e) {
      showToast(errorText(e, failedText), 'error');
    } finally {
      setStarting(false);
    }
  };

  const onSync = () =>
    startSync(
      syncSeoInventoryMutation(),
      'syncSeoInventory',
      'inventorySync',
      setStartingSync,
      'Reading the site. This takes a minute or two; you can keep working.',
      'The sync did not start.',
    );

  /*
   * Its own action rather than a second step of "Sync now": reading the site takes seconds,
   * reading Search Console and inspecting every URL with Google takes much longer and is
   * rate-limited, so one should never have to wait on the other.
   */
  const onSyncGoogle = () =>
    startSync(
      syncSearchPerformanceMutation(),
      'syncSearchPerformance',
      'searchSync',
      setStartingGoogle,
      'Refreshing Google data. This takes a few minutes; you can leave this page.',
      'Google data was not refreshed.',
    );

  const onExport = async () => {
    setExporting(true);
    const day = new Date().toISOString().slice(0, 10);
    try {
      if (tab === 'keywords')
        downloadCsv(`seo-keywords-${day}.csv`, await keywordsCsv(keywords));
      else downloadCsv(`seo-pages-${day}.csv`, await pagesCsv(state));
    } catch (e) {
      showToast(errorText(e, 'The export failed.'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const lastRead = [
    summary?.lastSyncedAt && `Site read ${formatDateTime(summary.lastSyncedAt)}`,
    summary?.searchSyncedAt &&
      `Google data ${formatDateTime(summary.searchSyncedAt)}`,
  ].filter(Boolean);

  return (
    <CRMPageTemplate
      icon={<TravelExploreOutlinedIcon />}
      title="SEO, GEO, AEO"
      subtitle={
        'Every search page, how Google sees it, and everything that has changed on it.' +
        (lastRead.length ? ` ${lastRead.join('. ')}.` : '')
      }
      totalCount={summary?.total}
      tabs={{
        value: TABS.indexOf(tab),
        onChange: onTab,
        tabs: [
          { label: 'Pages', icon: <WebOutlinedIcon /> },
          { label: 'Categories', icon: <CategoryOutlinedIcon /> },
          { label: 'Keywords', icon: <KeyOutlinedIcon /> },
          { label: 'Activity', icon: <HistoryOutlinedIcon /> },
        ],
      }}
      badge={canEdit ? undefined : <ViewOnlyChip />}
      headerActions={
        <>
          {canEdit && (
            <ActionIconButton
              label={startingSync || syncRunning ? 'Reading the site' : 'Sync now'}
              icon={<SyncOutlinedIcon />}
              busy={startingSync || syncRunning}
              onClick={onSync}
            />
          )}
          {canEdit && (
            <ActionIconButton
              label={startingGoogle || googleRunning ? 'Reading Google' : 'Refresh Google data'}
              icon={<CloudSyncOutlinedIcon />}
              busy={startingGoogle || googleRunning}
              onClick={onSyncGoogle}
            />
          )}
          <ActionIconButton
            label={tab === 'keywords' ? 'Export keywords CSV' : 'Export pages CSV'}
            icon={<FileDownloadOutlinedIcon />}
            busy={exporting}
            onClick={onExport}
          />
        </>
      }
    >
      <Box className="seo-list">
        {tab === 'pages' ? (
          <PagesTab
            state={state}
            setState={setState}
            summary={summary}
            summaryLoading={summaryLoading}
            reloadKey={reloadKey}
          />
        ) : tab === 'categories' ? (
          <SeoCategoriesTab role={role} setRole={setRole} reloadKey={reloadKey} />
        ) : tab === 'keywords' ? (
          <SeoKeywordsTab
            state={keywords}
            setState={setKeywords}
            summary={summary}
            reloadKey={reloadKey}
          />
        ) : (
          <ActivityTab reloadKey={reloadKey} />
        )}
      </Box>
    </CRMPageTemplate>
  );
};

export default Seo;
